import { useQuery } from "@tanstack/solid-query";
import type { Attachment } from "airtable";
import type { configType } from "~/js/network/network-handler";
import type { NodeDefinition } from "~/js/nodes/node-definition";
import { AirtableProxiApi } from "~/solidJs/shared/api/AirtableProxyApi";
import { queryClient } from "~/solidJs/shared/api/queryClient";

export type IMlBaseItem = {
	globalId: string;
	inputTypes: string[];
	outputTypes: string[];
	name: string;
	description: string;
	category: string;
	disabledPremiumText: string;
	tags: string[];
	neuralNetworks: string | string[];
	coverPictureUrl: string | null;
	stickerImageUrl: string | null;
	stickerLearnMoreUrl: string | null;
	isNeedToVariate: boolean;
	selectedPremiumPlan?: string;
	premiumBannerText?: string;
	isNeedToSynchOutputs: boolean;
	isNeedToAutoMask: boolean;
	isNeedToPromptEnhance: boolean;
	isNeedToDescribeImage: boolean;
	isNeedToMjUpscale: boolean;
	isNeedToMjVariate: boolean;
	isNeedToRevertSocketOrder: boolean;
	isNeedToMjZoomOut: boolean;
	isNeedToMjPan: boolean;
	isNeedToVision: boolean;
	isNeedToVariationsPro: boolean;
	isNeedToPromptVariations: boolean;
	isNeedToReimagine: boolean;
	isNeedToRandomModels: boolean;
	isNeedToRecolor: boolean;
	isNeedToRestyle: boolean;
	isNeedToCrop: boolean;
	isNeedToDetailedUpscale: boolean;
	isNeedToUpscale: boolean;
	isNeedToFaceRestore: boolean;
	isNeedToDownscale: boolean;
	searchTags?: string;
	isNeedToGenPrompt: boolean;
	isNeedToFetchModels: boolean;
	isNeedToFetchLoras: boolean;
	isNeedToSvg: boolean;
	isNeedToOutPaint: boolean;
	isNeedToZoomOut: boolean;
	isNeedDalleGenerate: boolean;
	isNeedToAddStyle: boolean;
	disabledBanner: string | null;
	isEnglishOnly: boolean;
	/** optionaly add checkpoint socket */
	pplusmodels?: string;
	/** optionaly add prompt style socket */
	promptStyles?: string;
	/** modifies ml sokets data */
	socketsToModify?: string;
	socketsDisplay: Record<string, string>;
	/**
	 * is item a custom element such as comment frame or other custom object
	 */
	isCustomElement?: boolean;
	node?: NodeDefinition;
	/**
	 * stores splited custom nodes in promise e.g 3d node that uses threejs, cropper nodes, compose node that uses fabric js
	 */
	promise?: Promise<NodeDefinition>;
	instructionText: string;
	instructionGif?: string;
	/**median time in seconds */
	medianTime: number;
	/**
	 * used to show badge in sidebar
	 * @deprecated use string to show custom label message
	 */
	isBeta?: boolean;
	labelText?: string;
	isPremium?: boolean;
};
type promptStylesKey = `promptStyles_${configType["GRAYLOG_BUILD_NAME"]}`;
type StylesKey = `isNeedToAddStyle_${configType["GRAYLOG_BUILD_NAME"]}`;
type SocketsToModify = `SocketsToModify_${configType["GRAYLOG_BUILD_NAME"]}`;
type SocketsDisplayKey = `Sockets Display JSON_${configType["GRAYLOG_BUILD_NAME"]}`;
export type IMlAirtableBaseItem = {
	instructionText: string;
	Title: string;
	"Global ID": string;
	Input: string[];
	Output: string[];
	disabledPremiumText: string;
	Name: string;
	Description: string;
	"Group of creator tasks": string;
	"Tags about usefulness": string[];
	"Neural network": string;
	/**
	 * @deprecated cache invalidation problem on backend cause images not loaded on user side
	 */
	Cover: Attachment[];
	CoverCdn: string;
	/**
	 * @deprecated cache invalidation problem on backend cause images not loaded on user side
	 */
	Sticker: Attachment[];
	"AI Library description": string;
	isNeedToVariate: boolean;
	isNeedToSynchOutputs: boolean;
	isNeedToAutoMask: boolean;
	isNeedToPromptEnhance: boolean;
	isNeedToDescribeImage: boolean;
	isNeedToMjUpscale: boolean;
	isNeedToMjVariate: boolean;
	isNeedToMjZoomOut: boolean;
	isNeedToMjPan: boolean;
	isNeedToRevertSocketOrder: boolean;
	isNeedToVariationsPro: boolean;
	isNeedToPromptVariations: boolean;
	isNeedToReimagine: boolean;
	isNeedToRecolor: boolean;
	isNeedToRestyle: boolean;
	isNeedToCrop: boolean;
	isNeedToVision: boolean;
	isNeedToRandomModels: boolean;
	isNeedToDetailedUpscale: boolean;
	isNeedToUpscale: boolean;
	isNeedToFaceRestore: boolean;
	isNeedToDownscale: boolean;
	searchTags?: string;
	isNeedToGenPrompt: boolean;
	isNeedToOutPaint: boolean;
	isNeedDalleGenerate: boolean;
	isNeedToZoomOut: boolean;
	isNeedToFetchModels: boolean;
	isNeedToFetchLoras: boolean;
	isNeedToSvg: boolean;
	/**
	 * @deprecated cache invalidation problem on backend cause images not loaded on user side
	 */
	disabledBanner: Attachment[];
	disabledBannerCdn: string;
	/**
	 * @deprecated cache invalidation problem on backend cause images not loaded on user side
	 */
	instructionGif: Attachment[];
	instructionGifUrl?: string;
	selectedPremiumPlan?: string;
	premiumBannerHeader?: string;
	isPremium: boolean;
	/**median time in seconds */
	medianTime: number;
	/**
	 * used to show badge in sidebar
	 * @deprecated use labelText to show custom label message
	 */
	isBeta?: boolean;
	labelText?: string;
	isEnglishOnly: boolean;
} & {
	[k in SocketsToModify]?: string;
} & {
	[k in SocketsDisplayKey]?: string;
} & {
	[k in promptStylesKey]?: string;
} & {
	[k in StylesKey]?: boolean;
};

/**
 * fetches ml base data from airtable, saving data to cache client
 * @todo get rid of airtable data layer, fetch every thing from one source
 */
const getMLBase = () => {
	return queryClient.fetchQuery(AirtableProxiApi.getfetchAndTransformMlBaseQueryOptions());
};

const getMLBaseQuery = () => {
	return useQuery(() => AirtableProxiApi.getfetchAndTransformMlBaseQueryOptions());
};

const AirtableMLBase = {
	get: getMLBase,
	getMLBaseQuery
};

export default AirtableMLBase;
